"use client";

import type { DietJsonldDietRead } from "@repo/api/catalog/api/schemas/diet-jsonld-diet-read";
import { useLocale, useTranslations } from "next-intl";
import { useEffect } from "react";
import type { UseFormReturn } from "react-hook-form";
import { FormCheckbox } from "@/components/form";
import { FormCounterInput } from "@/components/form/form-counter-input";
import { FormDatePicker } from "@/components/form/form-date-picker";
import { DelveryDateBanner } from "@/features/cart/components/configure-diet/delivery-date-banner";
import { LengthDiscountStepper } from "@/features/cart/components/discount/length-discount-stepper";
import type {
  ConfigureDietFormType,
  ConfigureDietReturnFormType,
} from "@/features/cart/forms/configure-diet";
import type { CartPricing } from "@/features/cart/types";
import type { LengthDiscount } from "@/features/diets/types/length-discount.type";
import { useDietDates } from "@/lib/hooks/use-diet-dates";

const SPECIAL_MERCHANT_ID = "c70ed38d-c7be-475a-8f06-aae4e1bba154";
const SPECIAL_MERCHANT_DATE = new Date(2026, 3, 3);

const isSpecialMerchantDate = (date: Date) =>
  date.toDateString() === SPECIAL_MERCHANT_DATE.toDateString();

export const CartCalendar = ({
  form,
  diet,
  pricing,
  loading,
  displayLengthDiscounts = true,
}: {
  form: UseFormReturn<ConfigureDietFormType, unknown, ConfigureDietFormType>;
  diet: DietJsonldDietRead;
  pricing?: CartPricing;
  loading?: boolean;
  displayLengthDiscounts?: boolean;
}) => {
  const locale = useLocale();
  const t = useTranslations();
  const daysToSelect = form.watch("daysToSelect");
  const dates = form.watch("dates");
  const isSpecialMerchant = diet.catering?.id === SPECIAL_MERCHANT_ID;
  const dietDates = useDietDates({
    diet,
    saturdays: form.getValues("saturdays"),
    sundays: form.getValues("sundays"),
    locale,
  });
  const isSpecialMerchantConfigured =
    !isSpecialMerchant ||
    (daysToSelect === 1 &&
      dates &&
      dates.length === 1 &&
      dates[0] &&
      isSpecialMerchantDate(new Date(dates[0])) &&
      !form.getValues("saturdays") &&
      !form.getValues("sundays"));

  const setDays = (days: number) => {
    form.setValue("daysToSelect", days);
  };

  useEffect(() => {
    if (!isSpecialMerchant || isSpecialMerchantConfigured) {
      return;
    }

    const specialMerchantDate = new Date(SPECIAL_MERCHANT_DATE);

    form.setValue("daysToSelect", 1, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    form.setValue("dates", [specialMerchantDate], {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    form.setValue("saturdays", false, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    form.setValue("sundays", false, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [form, isSpecialMerchant, isSpecialMerchantConfigured]);

  const disabledDays = (
    diet.catering?.disabledDays?.map((day) => new Date(day)) || []
  ).filter((day) => !isSpecialMerchant || !isSpecialMerchantDate(day));

  if (isSpecialMerchant && !isSpecialMerchantConfigured) {
    return null;
  }

  return (
    <div className="space-y-4">
      {locale === "cz" && <DelveryDateBanner />}
      {diet.lengthDiscounts && displayLengthDiscounts && !isSpecialMerchant && (
        <LengthDiscountStepper
          currentDays={daysToSelect}
          lengthDiscount={diet.lengthDiscounts as unknown as LengthDiscount}
          loading={loading}
          pricing={pricing}
          setDays={setDays}
        />
      )}
      <div className="flex w-full flex-col gap-4 max-md:justify-between md:flex-row md:items-center">
        {isSpecialMerchant ? (
          <div className="flex h-14 w-full items-center rounded-md border border-input bg-background px-4 font-medium text-base md:h-16 md:max-w-[240px]">
            {t("common.days", { count: 1 })}
          </div>
        ) : (
          <FormCounterInput
            fieldName="daysToSelect"
            fieldPlaceholder={t("filters.labels.days")}
            form={form}
            layout="big"
            sufix={"days"}
          />
        )}
        {!isSpecialMerchant &&
          (diet.catering?.saturdayDelivery ||
            diet.catering?.sundayDelivery) && (
            <div className="flex items-center gap-6 max-md:mt-2 md:ml-auto md:flex-row">
              {diet.catering.saturdayDelivery && (
                <FormCheckbox
                  data-pw="saturdays-checkbox"
                  fieldLabel={t("cart.calendar.saturdays")}
                  fieldName="saturdays"
                  form={form}
                />
              )}
              {diet.catering.sundayDelivery && (
                <FormCheckbox
                  data-pw="sundays-checkbox"
                  fieldLabel={t("cart.calendar.sundays")}
                  fieldName="sundays"
                  form={form}
                />
              )}
            </div>
          )}
      </div>
      <div
        className={
          isSpecialMerchant ? "pointer-events-none select-none" : undefined
        }
        id="calendar-date-picker"
      >
        <FormDatePicker
          blockSelectingNewDaysWhenFull={isSpecialMerchant}
          daysToSelect={daysToSelect}
          disableAutoNextDays={isSpecialMerchant}
          disabledDays={disabledDays}
          disableSaturdays={!form.getValues("saturdays")}
          disableSundays={!form.getValues("sundays")}
          fieldName="dates"
          fieldNameDaysToSelect="daysToSelect"
          firstPossibleOrderDay={
            isSpecialMerchant
              ? SPECIAL_MERCHANT_DATE
              : dietDates.nextPossibleDay(new Date())
          }
          form={form}
          isDisabledDate={
            isSpecialMerchant ? (day) => !isSpecialMerchantDate(day) : undefined
          }
          lastPossibleOrderDay={dietDates.lastPossibleOrderDay}
          showClearButton={!isSpecialMerchant}
          showMessage={false}
        />
      </div>
    </div>
  );
};
